import type { BrowseMarketplaceOffers200 } from "@packages/api-client/types/marketplaceController";
import {
	BR_STATES,
	type BrazilState,
	MARKETPLACE_HEALTH_FLAGS,
	MARKETPLACE_OFFER_CATEGORIES,
	type MarketplaceHealthFlag,
	type MarketplaceOfferCategory,
	type MarketplaceOfferSort,
	type MarketplaceSexFilter,
} from "@packages/shared-types";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	Heart,
	LayoutGrid,
	List,
	MapPin,
	MessageCircle,
	PanelLeftClose,
	PanelLeftOpen,
	Search,
	SlidersHorizontal,
	Store,
	X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBrowseMarketplaceOffers } from "@/api/marketplace/offers";
import { AppDate } from "@/components/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MultiSelect } from "@/components/ui/multiselect";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils/utils";
import { CowPrintArt } from "./-components/CowPrintArt";
import { useFavorites } from "./-components/useFavorites";

/* -------------------------------------------------------------------------- */
/*  URL search contract — facet filters live in the URL, applied server-side  */
/* -------------------------------------------------------------------------- */

/** The buyer-grade facet filters, mirrored into the URL search params. */
interface MarketplaceSearch {
	category?: MarketplaceOfferCategory;
	regionCode?: BrazilState;
	sex?: MarketplaceSexFilter;
	breeds?: string[];
	healthFlags?: MarketplaceHealthFlag[];
	minPrice?: number;
	maxPrice?: number;
	minHeadCount?: number;
	maxHeadCount?: number;
	minWeightKg?: number;
	maxWeightKg?: number;
	minAgeMonths?: number;
	maxAgeMonths?: number;
	sort?: MarketplaceOfferSort;
}

const SORT_KEYS: MarketplaceOfferSort[] = ["recent", "priceAsc", "priceDesc"];

const CATEGORY_SET = new Set<string>(MARKETPLACE_OFFER_CATEGORIES);
const HEALTH_FLAG_SET = new Set<string>(MARKETPLACE_HEALTH_FLAGS);
const STATE_SET = new Set<string>(BR_STATES);
const SEX_SET = new Set<string>(["MALE", "FEMALE", "MIXED"]);

function asNumber(raw: unknown): number | undefined {
	const n = typeof raw === "number" ? raw : Number(raw);
	return Number.isFinite(n) && n >= 0 ? n : undefined;
}

function asStringArray(raw: unknown): string[] | undefined {
	const arr = Array.isArray(raw)
		? raw
		: typeof raw === "string" && raw.length > 0
			? raw.split(",")
			: [];
	const cleaned = arr
		.filter((v): v is string => typeof v === "string")
		.map((v) => v.trim())
		.filter((v) => v.length > 0);
	return cleaned.length > 0 ? cleaned : undefined;
}

/** Validate the raw search record into the typed, bounded filter contract. */
function validateMarketplaceSearch(
	search: Record<string, unknown>,
): MarketplaceSearch {
	const breeds = asStringArray(search.breeds);
	const healthFlags = asStringArray(search.healthFlags)?.filter((f) =>
		HEALTH_FLAG_SET.has(f),
	) as MarketplaceHealthFlag[] | undefined;

	return {
		category: CATEGORY_SET.has(search.category as string)
			? (search.category as MarketplaceOfferCategory)
			: undefined,
		regionCode: STATE_SET.has(search.regionCode as string)
			? (search.regionCode as BrazilState)
			: undefined,
		sex: SEX_SET.has(search.sex as string)
			? (search.sex as MarketplaceSexFilter)
			: undefined,
		breeds,
		healthFlags:
			healthFlags && healthFlags.length > 0 ? healthFlags : undefined,
		minPrice: asNumber(search.minPrice),
		maxPrice: asNumber(search.maxPrice),
		minHeadCount: asNumber(search.minHeadCount),
		maxHeadCount: asNumber(search.maxHeadCount),
		minWeightKg: asNumber(search.minWeightKg),
		maxWeightKg: asNumber(search.maxWeightKg),
		minAgeMonths: asNumber(search.minAgeMonths),
		maxAgeMonths: asNumber(search.maxAgeMonths),
		sort: SORT_KEYS.includes(search.sort as MarketplaceOfferSort)
			? (search.sort as MarketplaceOfferSort)
			: undefined,
	};
}

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/marketplace/")(
	{
		validateSearch: validateMarketplaceSearch,
		component: MarketplaceBrowsePage,
	},
);

type Offer = BrowseMarketplaceOffers200["data"][number];

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

function publishedDate(offer: Offer): Date {
	return new Date(offer.publishedAt ?? offer.createdAt);
}

function isFresh(offer: Offer): boolean {
	const days = (Date.now() - publishedDate(offer).getTime()) / 86_400_000;
	return days <= 7;
}

/** True when any server-side facet is active. */
function hasActiveFacets(s: MarketplaceSearch): boolean {
	return (
		s.category !== undefined ||
		s.regionCode !== undefined ||
		s.sex !== undefined ||
		(s.breeds?.length ?? 0) > 0 ||
		(s.healthFlags?.length ?? 0) > 0 ||
		s.minPrice !== undefined ||
		s.maxPrice !== undefined ||
		s.minHeadCount !== undefined ||
		s.maxHeadCount !== undefined ||
		s.minWeightKg !== undefined ||
		s.maxWeightKg !== undefined ||
		s.minAgeMonths !== undefined ||
		s.maxAgeMonths !== undefined
	);
}

/* -------------------------------------------------------------------------- */
/*  Filter rail                                                               */
/* -------------------------------------------------------------------------- */

/** A labelled min/max numeric range row. */
function RangeRow({
	label,
	minValue,
	maxValue,
	onMin,
	onMax,
}: {
	label: string;
	minValue?: number;
	maxValue?: number;
	onMin: (v?: number) => void;
	onMax: (v?: number) => void;
}) {
	const { t } = useTranslation();
	const parse = (raw: string): number | undefined => {
		if (raw === "") return undefined;
		const n = Number(raw);
		return Number.isFinite(n) && n >= 0 ? n : undefined;
	};
	return (
		<div>
			<h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-base-content/60">
				{label}
			</h3>
			<div className="flex items-center gap-2">
				<Input
					type="number"
					inputMode="numeric"
					min={0}
					placeholder={t("marketplace.browse.min")}
					value={minValue ?? ""}
					onChange={(e) => onMin(parse(e.target.value))}
					className="rounded-full"
				/>
				<span className="text-base-content/40">–</span>
				<Input
					type="number"
					inputMode="numeric"
					min={0}
					placeholder={t("marketplace.browse.max")}
					value={maxValue ?? ""}
					onChange={(e) => onMax(parse(e.target.value))}
					className="rounded-full"
				/>
			</div>
		</div>
	);
}

function FilterRail({
	breedOptions,
	value,
	onChange,
	onClear,
}: {
	breedOptions: string[];
	value: MarketplaceSearch;
	onChange: (patch: Partial<MarketplaceSearch>) => void;
	onClear: () => void;
}) {
	const { t } = useTranslation();

	return (
		<div className="space-y-6">
			{/* Production category */}
			<div>
				<h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-base-content/60">
					{t("marketplace.browse.productionCategory")}
				</h3>
				<div className="flex flex-wrap gap-2">
					{[undefined, ...MARKETPLACE_OFFER_CATEGORIES].map((c) => {
						const active = value.category === c;
						return (
							<button
								key={c ?? "ALL"}
								type="button"
								onClick={() => onChange({ category: c })}
								className={cn(
									"rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
									active
										? "border-primary bg-primary text-primary-content"
										: "border-base-300 bg-base-100 text-base-content/80 hover:border-primary/40 hover:bg-base-200",
								)}
							>
								{c === undefined
									? t("marketplace.browse.allCategories")
									: t(`marketplace.offerCategory.${c}`, c)}
							</button>
						);
					})}
				</div>
			</div>

			{/* State / region */}
			<div>
				<h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-base-content/60">
					{t("marketplace.browse.state")}
				</h3>
				<Select
					value={value.regionCode ?? "ALL"}
					onValueChange={(v) =>
						onChange({
							regionCode: v === "ALL" ? undefined : (v as BrazilState),
						})
					}
				>
					<SelectTrigger className="w-full rounded-full">
						<SelectValue placeholder={t("marketplace.browse.allStates")} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="ALL">
							{t("marketplace.browse.allStates")}
						</SelectItem>
						{BR_STATES.map((s) => (
							<SelectItem key={s} value={s}>
								{s}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			{/* Sex mix */}
			<div>
				<h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-base-content/60">
					{t("marketplace.browse.sex")}
				</h3>
				<div className="flex flex-wrap gap-2">
					{([undefined, "MALE", "FEMALE", "MIXED"] as const).map((sx) => {
						const active = value.sex === sx;
						return (
							<button
								key={sx ?? "ALL"}
								type="button"
								onClick={() => onChange({ sex: sx ?? undefined })}
								className={cn(
									"rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
									active
										? "border-primary bg-primary text-primary-content"
										: "border-base-300 bg-base-100 text-base-content/80 hover:border-primary/40 hover:bg-base-200",
								)}
							>
								{sx === undefined
									? t("marketplace.browse.allSexes")
									: t(`marketplace.sex.${sx}`, sx)}
							</button>
						);
					})}
				</div>
			</div>

			<RangeRow
				label={t("marketplace.browse.priceRange")}
				minValue={value.minPrice}
				maxValue={value.maxPrice}
				onMin={(v) => onChange({ minPrice: v })}
				onMax={(v) => onChange({ maxPrice: v })}
			/>

			<RangeRow
				label={t("marketplace.browse.animalCount")}
				minValue={value.minHeadCount}
				maxValue={value.maxHeadCount}
				onMin={(v) => onChange({ minHeadCount: v })}
				onMax={(v) => onChange({ maxHeadCount: v })}
			/>

			<RangeRow
				label={t("marketplace.browse.weightRange")}
				minValue={value.minWeightKg}
				maxValue={value.maxWeightKg}
				onMin={(v) => onChange({ minWeightKg: v })}
				onMax={(v) => onChange({ maxWeightKg: v })}
			/>

			<RangeRow
				label={t("marketplace.browse.ageRange")}
				minValue={value.minAgeMonths}
				maxValue={value.maxAgeMonths}
				onMin={(v) => onChange({ minAgeMonths: v })}
				onMax={(v) => onChange({ maxAgeMonths: v })}
			/>

			{/* Health certifications (contains-all) */}
			<div>
				<h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-base-content/60">
					{t("marketplace.browse.health")}
				</h3>
				<MultiSelect
					options={MARKETPLACE_HEALTH_FLAGS.map((f) => ({
						value: f,
						label: t(`marketplace.healthFlag.${f}`, f),
					}))}
					value={value.healthFlags ?? []}
					onChange={(flags) =>
						onChange({ healthFlags: flags as MarketplaceHealthFlag[] })
					}
					placeholder={t("marketplace.browse.allHealth")}
					className="w-full rounded-full"
					showSelectAll={false}
					selectedCountText={t("marketplace.browse.healthSelected", {
						count: value.healthFlags?.length ?? 0,
					})}
				/>
			</div>

			{/* Breeds (overlap) — options come from the current page's offers */}
			{breedOptions.length > 0 && (
				<div>
					<h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-base-content/60">
						{t("marketplace.browse.breed")}
					</h3>
					<MultiSelect
						options={breedOptions.map((b) => ({ value: b, label: b }))}
						value={value.breeds ?? []}
						onChange={(breeds) => onChange({ breeds })}
						placeholder={t("marketplace.browse.allBreeds")}
						className="w-full rounded-full"
						showSelectAll={false}
						selectedCountText={t("marketplace.browse.breedsSelected", {
							count: value.breeds?.length ?? 0,
						})}
					/>
				</div>
			)}

			{hasActiveFacets(value) && (
				<Button
					variant="ghost"
					size="sm"
					onClick={onClear}
					className="text-base-content/60 hover:text-base-content"
				>
					<X className="size-4" />
					{t("marketplace.browse.clearFilters")}
				</Button>
			)}
		</div>
	);
}

/* -------------------------------------------------------------------------- */
/*  Offer card (grid) & row (list)                                            */
/* -------------------------------------------------------------------------- */

function PriceBlock({ offer, large }: { offer: Offer; large?: boolean }) {
	const { t } = useTranslation();
	const big =
		offer.askingTotal != null
			? `${offer.currency} ${offer.askingTotal.toLocaleString()}`
			: `${offer.currency} ${offer.unitPrice.toLocaleString()}`;
	const sub =
		offer.askingTotal != null
			? t("marketplace.headCount", { n: offer.headCount })
			: t(
					`livestock.sales.pricingModels.${offer.pricingModel}`,
					offer.pricingModel,
				);

	return (
		<div>
			<p
				className={cn(
					"font-bold leading-none text-primary",
					large ? "text-2xl" : "text-xl",
				)}
			>
				{big}
			</p>
			<p className="mt-1 text-xs text-base-content/60">
				{offer.askingTotal != null ? null : "/ "}
				{sub}
			</p>
		</div>
	);
}

function FavoriteButton({
	active,
	onToggle,
}: {
	active: boolean;
	onToggle: () => void;
}) {
	const { t } = useTranslation();
	return (
		<button
			type="button"
			aria-label={t("marketplace.browse.save")}
			aria-pressed={active}
			onClick={(e) => {
				e.preventDefault();
				e.stopPropagation();
				onToggle();
			}}
			className={cn(
				"relative z-10 grid size-9 place-items-center rounded-full bg-base-100/90 shadow-sm backdrop-blur transition-transform hover:scale-110 active:scale-95",
				active ? "text-error" : "text-base-content/60",
			)}
		>
			<Heart className={cn("size-5", active && "fill-current")} />
		</button>
	);
}

/** Offer cover photo when present, generated art as the fallback. */
function OfferThumb({
	offer,
	className,
}: {
	offer: Offer;
	className?: string;
}) {
	if (offer.coverImageUrl) {
		return (
			<img
				src={offer.coverImageUrl}
				alt={offer.title}
				className={cn("object-cover", className)}
			/>
		);
	}
	return <CowPrintArt seed={offer.id} className={className} />;
}

function OfferGridCard({
	offer,
	orgId,
	favorited,
	onToggleFavorite,
}: {
	offer: Offer;
	orgId: string;
	favorited: boolean;
	onToggleFavorite: () => void;
}) {
	const { t } = useTranslation();
	return (
		<div className="group relative flex flex-col overflow-hidden rounded-xl border border-base-300 bg-base-100 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
			<div className="relative aspect-[4/3] overflow-hidden">
				<OfferThumb
					offer={offer}
					className="size-full transition-transform duration-500 group-hover:scale-105"
				/>
				<span className="absolute left-3 top-3 rounded-full bg-base-100/90 px-2.5 py-1 text-xs font-semibold text-base-content shadow-sm backdrop-blur">
					{t(`marketplace.kind.${offer.offerKind}`, offer.offerKind)}
				</span>
				<div className="absolute right-3 top-3">
					<FavoriteButton active={favorited} onToggle={onToggleFavorite} />
				</div>
				<span className="absolute bottom-3 left-3 rounded-md bg-stone-900/70 px-2 py-1 text-xs font-medium text-white">
					{t("marketplace.headCount", { n: offer.headCount })}
				</span>
				{isFresh(offer) && (
					<span className="absolute bottom-3 right-3 rounded-md bg-success px-2 py-1 text-xs font-semibold text-success-content shadow">
						{t("marketplace.browse.newBadge")}
					</span>
				)}
			</div>

			<div className="flex flex-1 flex-col gap-2 p-4">
				<Link
					to="/app/org/$orgId/marketplace/$offerId"
					params={{ orgId, offerId: offer.id }}
					className="after:absolute after:inset-0"
				>
					<h2 className="line-clamp-2 min-h-[2.5rem] font-medium leading-snug text-base-content group-hover:text-primary">
						{offer.title}
					</h2>
				</Link>

				<PriceBlock offer={offer} />

				<div className="mt-auto flex items-center gap-1.5 pt-1 text-xs text-base-content/60">
					<MapPin className="size-3.5 shrink-0" />
					<span className="truncate">{offer.sellerOrgName}</span>
					{offer.regionCode && (
						<>
							<span aria-hidden>·</span>
							<span className="whitespace-nowrap">{offer.regionCode}</span>
						</>
					)}
					<span aria-hidden>·</span>
					<span className="whitespace-nowrap">
						<AppDate value={publishedDate(offer)} />
					</span>
				</div>

				<Button
					asChild
					variant="outline"
					size="sm"
					className="relative z-10 mt-2 w-full rounded-full border-primary/40 text-primary hover:bg-primary hover:text-primary-content"
				>
					<Link
						to="/app/org/$orgId/marketplace/$offerId"
						params={{ orgId, offerId: offer.id }}
					>
						<MessageCircle className="size-4" />
						{t("marketplace.browse.contact")}
					</Link>
				</Button>
			</div>
		</div>
	);
}

function OfferListRow({
	offer,
	orgId,
	favorited,
	onToggleFavorite,
}: {
	offer: Offer;
	orgId: string;
	favorited: boolean;
	onToggleFavorite: () => void;
}) {
	const { t } = useTranslation();
	return (
		<div className="group relative flex gap-4 rounded-xl border border-base-300 bg-base-100 p-3 shadow-sm transition-colors hover:border-primary/40 hover:bg-base-200/50">
			<div className="relative size-28 shrink-0 overflow-hidden rounded-lg sm:size-36">
				<OfferThumb offer={offer} className="size-full" />
				<span className="absolute bottom-1.5 left-1.5 rounded bg-stone-900/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
					{t("marketplace.headCount", { n: offer.headCount })}
				</span>
			</div>

			<div className="flex min-w-0 flex-1 flex-col">
				<div className="flex items-start justify-between gap-2">
					<Link
						to="/app/org/$orgId/marketplace/$offerId"
						params={{ orgId, offerId: offer.id }}
						className="min-w-0 after:absolute after:inset-0"
					>
						<h2 className="line-clamp-2 font-medium leading-snug text-base-content group-hover:text-primary">
							{offer.title}
						</h2>
					</Link>
					<FavoriteButton active={favorited} onToggle={onToggleFavorite} />
				</div>

				<span className="mt-1 inline-flex w-fit items-center rounded-full bg-base-200 px-2 py-0.5 text-xs font-medium text-base-content/70">
					{t(`marketplace.kind.${offer.offerKind}`, offer.offerKind)}
				</span>

				<div className="mt-2">
					<PriceBlock offer={offer} large />
				</div>

				<div className="mt-auto flex items-center gap-1.5 pt-2 text-xs text-base-content/60">
					<MapPin className="size-3.5 shrink-0" />
					<span className="truncate">{offer.sellerOrgName}</span>
					{offer.regionCode && (
						<>
							<span aria-hidden>·</span>
							<span className="whitespace-nowrap">{offer.regionCode}</span>
						</>
					)}
					<span aria-hidden>·</span>
					<span className="whitespace-nowrap">
						<AppDate value={publishedDate(offer)} />
					</span>
				</div>
			</div>
		</div>
	);
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

const BROWSE_PAGE_LIMIT = 50;

function MarketplaceBrowsePage() {
	const { orgId } = Route.useParams();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const { t } = useTranslation();

	// Facets + sort are applied server-side, across the whole catalog.
	const { data, isLoading } = useBrowseMarketplaceOffers({
		page: 1,
		limit: BROWSE_PAGE_LIMIT,
		category: search.category,
		regionCode: search.regionCode,
		sex: search.sex,
		breeds: search.breeds?.join(","),
		healthFlags: search.healthFlags?.join(","),
		minPrice: search.minPrice,
		maxPrice: search.maxPrice,
		minHeadCount: search.minHeadCount,
		maxHeadCount: search.maxHeadCount,
		minWeightKg: search.minWeightKg,
		maxWeightKg: search.maxWeightKg,
		minAgeMonths: search.minAgeMonths,
		maxAgeMonths: search.maxAgeMonths,
		sort: search.sort ?? "recent",
	});
	const offers = useMemo(() => data?.data ?? [], [data]);

	const { favorites, toggle } = useFavorites();
	const [view, setView] = useState<"grid" | "list">("grid");
	// Free-text search is a client-side refinement over the loaded page — the
	// catalog API has no text-search param (a documented follow-up).
	const [textSearch, setTextSearch] = useState("");
	const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

	// Collapsed desktop filter rail (gives the grid full width), persisted.
	const [filtersCollapsed, setFiltersCollapsed] = useState<boolean>(() => {
		try {
			return (
				localStorage.getItem("ruralops-marketplace-filters-collapsed") === "1"
			);
		} catch {
			return false;
		}
	});
	useEffect(() => {
		try {
			localStorage.setItem(
				"ruralops-marketplace-filters-collapsed",
				filtersCollapsed ? "1" : "0",
			);
		} catch {
			// Ignore storage failures — collapse still works for the session.
		}
	}, [filtersCollapsed]);

	const breedOptions = useMemo(
		() => [...new Set(offers.flatMap((o) => o.breeds))].sort(),
		[offers],
	);

	const patchSearch = useCallback(
		(patch: Partial<MarketplaceSearch>) => {
			navigate({ search: (prev) => ({ ...prev, ...patch }) });
		},
		[navigate],
	);

	const clearFilters = useCallback(() => {
		navigate({ search: (prev) => ({ sort: prev.sort }) });
	}, [navigate]);

	// Client-side text refinement only — facets are already applied server-side.
	const visible = useMemo(() => {
		const q = textSearch.trim().toLowerCase();
		if (!q) return offers;
		return offers.filter((o) => {
			const hay =
				`${o.title} ${o.sellerOrgName} ${o.breeds.join(" ")}`.toLowerCase();
			return hay.includes(q);
		});
	}, [offers, textSearch]);

	const navLinks = (
		<div className="flex flex-wrap gap-2">
			<Button asChild>
				<Link to="/app/org/$orgId/marketplace/my-offers" params={{ orgId }}>
					{t("marketplace.browse.myOffers")}
				</Link>
			</Button>
		</div>
	);

	return (
		<div className="mx-auto w-full max-w-7xl">
			{/* Hero / heading */}
			<div className="border-b border-base-300 bg-base-100 px-4 py-5 sm:px-6">
				<div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
					<div className="flex items-center gap-3">
						<div className="grid size-11 place-items-center rounded-xl bg-primary text-primary-content shadow-sm">
							<Store className="size-6" />
						</div>
						<div>
							<h1 className="text-2xl font-bold tracking-tight">
								{t("marketplace.browse.title")}
							</h1>
							<p className="text-sm text-base-content/60">
								{t("marketplace.browse.subtitle")}
							</p>
						</div>
					</div>
					{navLinks}
				</div>
			</div>

			{/* Sticky search + sort toolbar */}
			<div className="sticky top-0 z-20 border-b border-base-300 bg-base-100/90 px-4 py-3 backdrop-blur sm:px-6">
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
					<div className="relative flex-1">
						<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-base-content/40" />
						<Input
							value={textSearch}
							onChange={(e) => setTextSearch(e.target.value)}
							placeholder={t("marketplace.browse.search")}
							className="h-11 rounded-full pl-10"
						/>
					</div>

					<div className="flex items-center gap-2">
						<Button
							variant="outline"
							className="rounded-full lg:hidden"
							onClick={() => setMobileFiltersOpen((v) => !v)}
						>
							<SlidersHorizontal className="size-4" />
							{t("marketplace.browse.filters")}
						</Button>

						{filtersCollapsed && (
							<Button
								variant="outline"
								className="hidden rounded-full lg:inline-flex"
								onClick={() => setFiltersCollapsed(false)}
							>
								<PanelLeftOpen className="size-4" />
								{t("marketplace.browse.filters")}
							</Button>
						)}

						<Select
							value={search.sort ?? "recent"}
							onValueChange={(v) =>
								patchSearch({ sort: v as MarketplaceOfferSort })
							}
						>
							<SelectTrigger className="h-11 w-44 rounded-full">
								<SelectValue placeholder={t("marketplace.browse.sort")} />
							</SelectTrigger>
							<SelectContent>
								{SORT_KEYS.map((k) => (
									<SelectItem key={k} value={k}>
										{t(
											`marketplace.browse.sort${k.charAt(0).toUpperCase()}${k.slice(1)}`,
										)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>

						<div className="flex rounded-full border border-base-300 p-0.5">
							<button
								type="button"
								aria-label={t("marketplace.browse.viewGrid")}
								onClick={() => setView("grid")}
								className={cn(
									"grid size-9 place-items-center rounded-full transition-colors",
									view === "grid"
										? "bg-primary text-primary-content"
										: "text-base-content/60 hover:bg-base-200",
								)}
							>
								<LayoutGrid className="size-4" />
							</button>
							<button
								type="button"
								aria-label={t("marketplace.browse.viewList")}
								onClick={() => setView("list")}
								className={cn(
									"grid size-9 place-items-center rounded-full transition-colors",
									view === "list"
										? "bg-primary text-primary-content"
										: "text-base-content/60 hover:bg-base-200",
								)}
							>
								<List className="size-4" />
							</button>
						</div>
					</div>
				</div>

				{/* Mobile collapsible filters */}
				{mobileFiltersOpen && (
					<div className="mt-3 rounded-xl border border-base-300 bg-base-100 p-4 lg:hidden">
						<FilterRail
							breedOptions={breedOptions}
							value={search}
							onChange={patchSearch}
							onClear={clearFilters}
						/>
					</div>
				)}
			</div>

			{/* Body: sidebar + results */}
			<div className="flex gap-6 py-6">
				{!filtersCollapsed && (
					<aside className="hidden w-60 shrink-0 lg:block">
						<div className="sticky top-20 rounded-xl border border-base-300 bg-base-100 p-5">
							<div className="mb-4 flex items-center justify-between gap-2">
								<h2 className="flex items-center gap-2 font-semibold">
									<SlidersHorizontal className="size-4" />
									{t("marketplace.browse.filters")}
								</h2>
								<button
									type="button"
									onClick={() => setFiltersCollapsed(true)}
									title={t("common.collapse")}
									aria-label={t("common.collapse")}
									className="flex size-8 items-center justify-center rounded-md text-base-content/50 transition-colors hover:bg-base-200 hover:text-base-content"
								>
									<PanelLeftClose className="size-4" />
								</button>
							</div>
							<FilterRail
								breedOptions={breedOptions}
								value={search}
								onChange={patchSearch}
								onClear={clearFilters}
							/>
						</div>
					</aside>
				)}

				<div className="min-w-0 flex-1">
					<p className="mb-4 text-sm text-base-content/60">
						{!isLoading &&
							t("marketplace.browse.resultsCount", { count: visible.length })}
					</p>

					{isLoading && (
						<div
							className={cn(
								"grid gap-4",
								"grid-cols-1 sm:grid-cols-2 xl:grid-cols-3",
							)}
						>
							{["a", "b", "c", "d", "e", "f"].map((sk) => (
								<div
									key={sk}
									className="overflow-hidden rounded-xl border border-base-300 bg-base-100"
								>
									<div className="aspect-[4/3] animate-pulse bg-base-200" />
									<div className="space-y-2 p-4">
										<div className="h-4 w-3/4 animate-pulse rounded bg-base-200" />
										<div className="h-6 w-1/2 animate-pulse rounded bg-base-200" />
										<div className="h-3 w-2/3 animate-pulse rounded bg-base-200" />
									</div>
								</div>
							))}
						</div>
					)}

					{!isLoading && offers.length === 0 && !hasActiveFacets(search) && (
						<div className="rounded-xl border border-dashed border-base-300 py-16 text-center text-base-content/60">
							<Store className="mx-auto mb-3 size-8 opacity-40" />
							{t("marketplace.browse.empty")}
						</div>
					)}

					{!isLoading &&
						visible.length === 0 &&
						(offers.length > 0 || hasActiveFacets(search)) && (
							<div className="rounded-xl border border-dashed border-base-300 py-16 text-center">
								<p className="text-base-content/60">
									{t("marketplace.browse.noResults")}
								</p>
								<Button
									variant="outline"
									size="sm"
									className="mt-4 rounded-full"
									onClick={() => {
										clearFilters();
										setTextSearch("");
									}}
								>
									{t("marketplace.browse.clearFilters")}
								</Button>
							</div>
						)}

					{!isLoading && visible.length > 0 && view === "grid" && (
						<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
							{visible.map((offer) => (
								<OfferGridCard
									key={offer.id}
									offer={offer}
									orgId={orgId}
									favorited={favorites.has(offer.id)}
									onToggleFavorite={() => toggle(offer.id)}
								/>
							))}
						</div>
					)}

					{!isLoading && visible.length > 0 && view === "list" && (
						<div className="flex flex-col gap-3">
							{visible.map((offer) => (
								<OfferListRow
									key={offer.id}
									offer={offer}
									orgId={orgId}
									favorited={favorites.has(offer.id)}
									onToggleFavorite={() => toggle(offer.id)}
								/>
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
